import { FCC } from "@/types/common";
import { useForm, useWatch } from "react-hook-form";
import { Box, Stack } from "@mui/material";
import { Form } from "@/components/elements/Form";
import { yupResolver } from "@hookform/resolvers/yup";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { Button } from "@/components/elements/Button";
import { JobTitle } from "@/features/job/components/JobTitle";
import { useGetJobSchema } from "@/features/job/hooks/useGetJobSchema";
import { TJobForm } from "@/features/job/types/JobForm";
import { JobStatus } from "@/features/job/components/job-form/JobStatus";
import { JobBasicInfo } from "@/features/job/components/job-form/JobBasicInfo";
import { JobEmploymentCondition } from "@/features/job/components/job-form/JobEmploymentCondition";
import { JobWorkingCondition } from "@/features/job/components/job-form/JobWorkingCondition";
import { JobRequirement } from "@/features/job/components/job-form/JobRequirement";
import { JobBenefit } from "@/features/job/components/job-form/JobBenefit";
import { JobFeature } from "@/features/job/components/job-form/JobFeature";
import { CreateRecruitmentSourceModal } from "@/features/job/components/job-form/CreateRecruitmentSourceModal";
import { useVisible } from "@/hooks/useVisible";
import { useGetRecruitmentSourceList } from "@/api/services/main/recruitment-source";
import { JobUploadFiles } from "@/features/job/components/job-form/JobUploadFiles";
import { JOB_STATUS } from "@/types/enum";
import { cleanObject, convertJobFormDataReq } from "@/features/job/utils/convertJobFormData";
import { toast } from "@/utils/toast";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { AppRoutes } from "@/routes/config";
import { showError } from "@/utils/error";
import { createJobApi, updateJobApi } from "@/api/services/main/job";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { queryClient } from "@/constants/queryClient";
import { getDynamicRoute } from "@/utils/url";
import { IconSave } from "@/assets/icons";
import { CreateCompanyModal } from "@/features/job/components/job-form/CreateCompanyModal";
import { useCompanyList } from "@/api/services/main/company";
import { getFirstErrorPath } from "@/features/job/utils/getFirstErrorPath";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { JOB_WIDTH_FULL } from "@/features/job/constants/width";
import { isEqual } from "lodash";

type TJobFormProps = {
  defaultValues: Partial<TJobForm>;
};
export const JobForm: FCC<TJobFormProps> = ({ defaultValues }) => {
  const navigate = useNavigate()
  const { jobSchema } = useGetJobSchema();
  const { recruitmentSourceOptions, isLoading: isLoadingRecruitmentSource,  refetch: refetchRecruitmentSources } = useGetRecruitmentSourceList();
  const { companyOptions, isLoading: isLoadingCompanyList, refetch: refetchCompanyList } = useCompanyList({});
  const createRecruitmentSourceModal = useVisible();
  const createCompanyModal = useVisible();
  const { jobId } = useParams();
  const onboardingSupportRef = useRef<(HTMLDivElement | null)>(null);
  const customFieldsKeys = ["jobWorkingTimes", "onboardingSupport", "relatedFileTitles"];
  const [enableBlocker, setEnableBlocker] = useState(false)

  const methods = useForm<TJobForm>({
    defaultValues,
    resolver: yupResolver(jobSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const { control, handleSubmit } = methods;
  const watchValue = methods.watch();

  const status = useWatch({ control, name: "status" });
  const isDraft = status === JOB_STATUS.DRAFT;

  useEffect(() => {
    const hasChanged = !isEqual(cleanObject(watchValue), cleanObject(defaultValues));
    setEnableBlocker(hasChanged);
  }, [watchValue]);

  // Navigation helpers
  const getBackRoute = () => {
    return jobId ? getDynamicRoute(AppRoutes.jobDetail, { jobId }) : AppRoutes.jobList;
  };

  // Back button handlers
  const handleBackClick = () => {
    navigate(getBackRoute());
  };

  const onSubmit = async (data: TJobForm) => {
    await handleSaveData(data);
  };

  const handleSaveData = async (data: TJobForm) => {
    try {
      const payload = convertJobFormDataReq(data);

      if (jobId) {
        await updateJobApi(jobId, payload);
        toast.success(API_SUCCESS_MESSAGE);

        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.JOB.FETCH_DETAIL, jobId],
        });
        const route = getDynamicRoute(AppRoutes.jobDetail, { jobId: jobId });
        navigate(route);
      } else {
        await createJobApi(payload);
        toast.success(API_SUCCESS_MESSAGE);
        navigate(AppRoutes.jobList);
      }
    } catch (e) {
      showError(e);
    }
  };

  // Check the first validation error for custom fields that require manual scrolling
  useEffect(() => {
    const currentErrors = methods.formState.errors;
    const isValidNow = methods.formState.isValid;

    if(isValidNow && currentErrors) {
      const firstErrorKey = Object.keys(currentErrors)[0];
      if (customFieldsKeys.includes(firstErrorKey) && currentErrors?.onboardingSupport ) {
        setTimeout(() => {
          onboardingSupportRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 0);
      }
    }

    if (!isValidNow && currentErrors) {
      const firstErrorKey = Object.keys(currentErrors)[0];

      // Only scroll if the first error key is one of the custom fields
      if (firstErrorKey && customFieldsKeys.includes(firstErrorKey)) {
        if (currentErrors?.jobWorkingTimes) {
          const firstErrorPath = getFirstErrorPath(currentErrors);
          const formatPathToId = (path: string) => path.replace(/\[(\d+)\]/g, "-$1").replace(/\./g, "-");
          const fieldId = firstErrorPath ? formatPathToId(firstErrorPath) : null;
          if (fieldId) {
            setTimeout(() => {
              const errorFieldElement = document.getElementById(fieldId);
              if (errorFieldElement) {
                errorFieldElement.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
              }
            }, 0);
          }
        } else if (currentErrors?.onboardingSupport) {
          setTimeout(() => {
            onboardingSupportRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }, 0);
        } else if (currentErrors?.relatedFileTitles) {
          const value = currentErrors.relatedFileTitles;
          let fieldId: string | null = null;
          if (Array.isArray(value)) {
            for (let i = 0; i < value.length; i++) {
              const item = value[i];
              if (item && typeof item === "object" && Object.keys(item).length > 0) {
                fieldId = `relatedFileTitles-${i}`;
                break;
              }
            }
          }
          if (fieldId) {
            setTimeout(() => {
              const errorFieldElement = document.getElementById(fieldId);
              if (errorFieldElement) {
                errorFieldElement.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
              }
            }, 0);
          }
        }
      }
    }

  }, [methods.formState.errors, methods.formState.isValid]);

  return (
    <>
      <JobTitle
        title={jobId ? defaultValues?.jobName : '新規求人作成'}
        tag={defaultValues?.tag}
        status={defaultValues?.status}
        createdAt={defaultValues?.createdAt}
        updatedAt={defaultValues?.updatedAt}
      />
      <Form methods={methods} onSubmit={onSubmit} schema={jobSchema} enableBlocker={enableBlocker}>
        <Box width={`${JOB_WIDTH_FULL}px`}>
          <MenuNavigate
            contents={[
              { title: "ステータス状況", content: <JobStatus isDraft={isDraft} /> },
              { title: "基本情報", content:
                <JobBasicInfo
                  isDraft={isDraft}
                  isLoadingCompany={isLoadingCompanyList}
                  companyOptions={companyOptions}
                  openCreateCompanyModal={createCompanyModal.open}
                  isLoadingRecruitmentSource={isLoadingRecruitmentSource}
                  recruitmentSourceOptions={recruitmentSourceOptions}
                  openCreateRecruitmentSourceModal={createRecruitmentSourceModal.open}
                />
              },
              { title: "雇用条件", content: <JobEmploymentCondition isDraft={isDraft} />},
              { title: "勤務条件", content: <JobWorkingCondition isDraft={isDraft} /> },
              { title: "応募要件", content: <JobRequirement isDraft={isDraft} /> },
              { title: "福利厚生", content: <JobBenefit isDraft={isDraft} /> },
              { title: "特性", content: <JobFeature isDraft={isDraft} onboardingSupportRef={onboardingSupportRef} /> },
              { title: "その他関連情報", content: <JobUploadFiles isDraft={isDraft} /> },
            ]}
            contentWidth="900px"
            contentAffix={(
              <FooterContainer>
                <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
                  <Button variant="ghost" onClick={handleBackClick}>
                    戻る
                  </Button>
                  <Stack direction="row" gap={3}>
                  <Button type="submit" variant="primary" startIcon={jobId ? <IconSave /> : <></>} size="sm" onClick={handleSubmit(onSubmit)}>
                    保存
                  </Button>
                </Stack>
                </Stack>
              </FooterContainer>
            )}
          />
        </Box>
      </Form>

      <CreateCompanyModal modal={createCompanyModal} refetchCompanyList={refetchCompanyList} />
      <CreateRecruitmentSourceModal modal={createRecruitmentSourceModal} refetchRecruitmentSources={refetchRecruitmentSources} />
    </>
  );
};
